import React, { useState, useEffect } from 'react';

/**
 * =============================================================================
 * IDS CRM Web - Shared customer-email modal (system rework R4c)
 * =============================================================================
 *
 * The one outbound-email shell (§0.8 pattern): pick a customer contact
 * (do-not-contact and missing-email disabled), optionally edit a draft
 * subject/body, then hand off to the caller's action — which builds/validates
 * the draft server-side and opens the user's OWN email client with the CRM
 * inbox CC'd. The CRM never sends customer email itself.
 *
 * Consumers: QuoteDetail (fixed draft + document downloads), TicketDetail
 * (editable closure draft, stamped on send).
 *
 * =============================================================================
 */

function EmailDraftModal({
  title,
  customerId,
  authFetch,
  onClose,
  // Preselect: explicit contact id first, else primary (requiring an email
  // address when preferEmailContact), else first contact (with email if preferred).
  defaultContactId = '',
  preferEmailContact = false,
  // When set, subject/body become editable fields initialised from this draft.
  initialDraft = null,
  // Extra JSX rendered between the picker and the footer (checkboxes, hints).
  children = null,
  actionLabel,
  busyLabel = 'Preparing…',
  actionTitle,
  // async (contactId, { subject, body }) — throw an Error to surface a message.
  onAction
}) {
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactId, setContactId] = useState(defaultContactId);
  const [subject, setSubject] = useState(initialDraft?.subject || '');
  const [body, setBody] = useState(initialDraft?.body || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const editable = initialDraft !== null;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setContactsLoading(true);
      try {
        const response = await authFetch(`/api/v1/contacts?customer_id=${customerId}&limit=100`);
        const data = await response.json();
        if (cancelled) return;
        const rows = data.data || [];
        setContacts(rows);
        if (!defaultContactId) {
          const primary = preferEmailContact
            ? (rows.find(c => c.is_primary && c.email) || rows.find(c => c.email))
            : (rows.find(c => c.is_primary) || rows[0]);
          if (primary) setContactId(primary.contact_id);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load contacts');
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authFetch, customerId, defaultContactId, preferEmailContact]);

  const handleAction = async () => {
    if (!contactId) { setError('Pick a contact'); return; }
    setBusy(true);
    setError(null);
    try {
      await onAction(contactId, { subject, body });
    } catch (err) {
      setError(err.message || 'Failed to prepare the email');
      setBusy(false);
    }
  };

  const selectedContact = contacts.find(c => c.contact_id === contactId);
  const canAct = Boolean(contactId)
    && !selectedContact?.do_not_contact
    && (!editable || (Boolean(subject.trim()) && Boolean(body.trim())));

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="email-draft-contact">Send to *</label>
            <select
              id="email-draft-contact"
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              disabled={contactsLoading}
            >
              <option value="">Select a contact…</option>
              {contacts.map(c => (
                <option key={c.contact_id} value={c.contact_id} disabled={c.do_not_contact || !c.email}>
                  {c.full_name}{c.email ? ` (${c.email})` : ' (no email)'}{c.do_not_contact ? ' — do not contact' : ''}
                </option>
              ))}
            </select>
          </div>

          {editable && (
            <>
              <div className="form-group">
                <label htmlFor="email-draft-subject">Subject *</label>
                <input
                  type="text"
                  id="email-draft-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email-draft-body">Message *</label>
                <textarea
                  id="email-draft-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                />
              </div>
            </>
          )}

          {children}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleAction}
            disabled={busy || !canAct}
            title={actionTitle || "Opens your own email client pre-filled, with the CRM inbox CC'd"}
          >
            {busy ? busyLabel : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailDraftModal;
